import { getOrderStatusEmailTemplate } from '@/emails/orderStatusTemplates';
import { logger } from '@/utils/logger';

export const STORAGE_KEY = 'email_templates';

// Get a template - always use default template since we're server-side
export const getTemplate = (templateId: string, orderData: any): string => {
  try {
    logger.debug(`Getting email template for status: ${templateId}`);

    // Validate orderData before processing
    if (!orderData) {
      logger.error('No order data provided for email template');
      return '';
    }

    // getOrderStatusEmailTemplate expects (order, status), not (status, order)
    const template = getOrderStatusEmailTemplate(orderData, templateId);

    if (!template) {
      logger.error(`No template found for status: ${templateId}`);
      return '';
    }

    logger.debug(`Template generated successfully for status: ${templateId}`);
    return template;
  } catch (error) {
    logger.error('Error getting template:', error);
    return ''; // Return empty string as a last resort
  }
};

// Save a custom template
export const saveTemplate = (templateId: string, content: string): boolean => {
  try {
    // Check if localStorage is available
    if (typeof window === 'undefined' || !window.localStorage) {
      logger.warn('localStorage not available, cannot save template');
      return false;
    }
    
    // Get existing templates
    const storedTemplates = localStorage.getItem(STORAGE_KEY);
    const templates = storedTemplates ? JSON.parse(storedTemplates) : {};
    
    // Update with new template
    templates[templateId] = content;
    
    // Save back to localStorage
    localStorage.setItem(STORAGE_KEY, JSON.stringify(templates));
    return true;
  } catch (error) {
    logger.error('Error saving template:', error);
    return false;
  }
};

// Delete a custom template
export const deleteTemplate = (templateId: string): boolean => {
  try {
    // Check if localStorage is available
    if (typeof window === 'undefined' || !window.localStorage) {
      logger.warn('localStorage not available, cannot delete template');
      return false;
    }
    
    // Get existing templates
    const storedTemplates = localStorage.getItem(STORAGE_KEY);
    if (!storedTemplates) return true; // Nothing to delete
    
    const templates = JSON.parse(storedTemplates);
    
    // If template doesn't exist, just return
    if (!templates[templateId]) return true;
    
    // Delete the template
    delete templates[templateId];
    
    // Save back to localStorage
    localStorage.setItem(STORAGE_KEY, JSON.stringify(templates));
    return true;
  } catch (error) {
    logger.error('Error deleting template:', error);
    return false;
  }
};

// Get all template IDs
export const getAllTemplates = (): Record<string, string> => {
  try {
    // Check if localStorage is available (important for SSR or tests)
    if (typeof window === 'undefined' || !window.localStorage) {
      logger.warn('localStorage not available');
      return {};
    }
    
    const storedTemplates = localStorage.getItem(STORAGE_KEY);
    return storedTemplates ? JSON.parse(storedTemplates) : {};
  } catch (error) {
    logger.error('Error getting all templates:', error);
    return {};
  }
}; 